import {
  FlexBox,
  Icon,
  Skeleton,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import classNames from 'classnames';
import { AnalyticsMenu } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { memo, useMemo } from 'react';
import { useEffectOnce } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { AppLink } from 'components/appLink';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardCard } from '../../../../../../components/card';
import { QueueDashboardMetrics } from '../metrics/queueDashboardMetrics';
import {
  QueueDashboardQueueContext,
  QueueDashboardQueueContextValue,
} from '../queue/queueDashboardQueue.context';

import styles from './queueDashboardOrganizationMetrics.module.scss';

export const QueueDashboardOrganizationMetrics = memo(
  function QueueDashboardOrganizationMetrics(): JSX.Element {
    const dispatch = useAppDispatch();

    const storage = useServerStorage('user');
    const { layout } = storage.data.dashboard ?? {};

    const { metrics, isSkeleton, refetch } =
      dashboardApi.useReadOrganizationDashboardMetricsQuery(undefined, {
        selectFromResult: ({ data, isUninitialized, isFetching, ...rest }) => ({
          ...rest,
          metrics: data?.data.organization_metrics[0]?.metrics,
          isSkeleton: isUninitialized || isFetching,
        }),
      });

    useEffectOnce(() => {
      dispatch(
        websocketActions.request({
          type: 'connection:subscribe',
          params: {
            type: 'real_time_dashboard',
            dashboard_type: 'organization',
          },
        }),
      );

      return () => {
        dispatch(
          websocketActions.request({
            type: 'connection:unsubscribe',
            params: {
              type: 'real_time_dashboard',
              dashboard_type: 'organization',
            },
          }),
        );
      };
    });

    useDashboardRefetchEffect({ refetch });

    const titleSlot = useMemo(
      (): JSX.Element => (
        <FlexBox alignItems="center" gap="200">
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            {isSkeleton ? (
              <Skeleton height="18px" width="18px" />
            ) : (
              <Tooltip
                content="The global metrics represent aggregate data from all queues in your organization."
                side="right"
                wrap="div"
                wrapProps={{ style: { display: 'flex' } }}
              >
                <Icon name="question" color={typography.color.secondary} />
              </Tooltip>
            )}
          </FlexBox>

          {isSkeleton ? (
            <Skeleton height="20px" width="140px" />
          ) : (
            <Text.Heading variant="heading-3">Global metrics</Text.Heading>
          )}
        </FlexBox>
      ),
      [isSkeleton],
    );

    const actionsSlot = useMemo(
      (): JSX.Element =>
        isSkeleton ? (
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            <Skeleton height="18px" width="6px" />
          </FlexBox>
        ) : (
          <AnalyticsMenu
            actions={[
              {
                type: 'link',
                label: 'TV mode',
                icon: 'presentation',
                link: <AppLink path="/tv/dashboard/organization" />,
                onClick: () => {
                  ampli.dashboardGlobalTvDashboardClicked();
                },
              },
            ]}
          />
        ),
      [isSkeleton],
    );

    const contextValue: QueueDashboardQueueContextValue = useMemo(
      () => ({
        isSkeleton,
        layout,
      }),
      [isSkeleton, layout],
    );

    const renderCompactLayout = (): JSX.Element => (
      <Table.Tr
        className={classNames({
          [styles.tr]: !isSkeleton,
        })}
      >
        <Table.Td>{titleSlot}</Table.Td>
        <QueueDashboardMetrics metrics={metrics} />
        <Table.Td>{actionsSlot}</Table.Td>
      </Table.Tr>
    );

    const renderDefaultLayout = (): JSX.Element => (
      <DashboardCard
        titleSlot={titleSlot}
        actionsSlot={actionsSlot}
        className={classNames({
          [styles.card]: !isSkeleton,
        })}
      >
        <QueueDashboardMetrics metrics={metrics} />
      </DashboardCard>
    );

    return (
      <QueueDashboardQueueContext.Provider value={contextValue}>
        {layout === 'compact' ? renderCompactLayout() : renderDefaultLayout()}
      </QueueDashboardQueueContext.Provider>
    );
  },
);
